import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Sparkles, Star, X, QrCode, CreditCard, ShieldCheck, Calendar, ExternalLink } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { useCurrentCharge } from "@/components/RenewalBanner";

export function PerfilPage() {
  const { user, usage } = useAuth();
  if (!user) return null;
  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Minha Conta</h1>
      <p className="mt-2 text-slate-600">Seus dados de acesso.</p>
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} referrerPolicy="no-referrer"
              className="w-16 h-16 rounded-2xl object-cover border border-slate-200"
              data-testid="profile-avatar"/>
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1F3A8A] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-2xl">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-heading text-xl font-bold text-slate-900" data-testid="profile-name">{user.name}</div>
            <div className="text-slate-500" data-testid="profile-email">{user.email}</div>
          </div>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Plano</div>
            <div className="mt-1 font-heading text-xl font-bold text-slate-900 capitalize">{user.plan}</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Gerações</div>
            <div className="mt-1 font-heading text-xl font-bold text-slate-900">{usage?.used ?? 0} / {usage?.limit ?? "-"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCpfCnpj(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{3})(\d)/, "$1.$2")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

function CheckoutModal({ open, onClose, user }) {
  const [cpf, setCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const digits = cpf.replace(/\D/g, "");
  const valid = digits.length === 11 || digits.length === 14;

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      try {
        const { trackInitiateCheckout } = await import("@/lib/pixel");
        trackInitiateCheckout({ value: 19.9, currency: "BRL" });
      } catch { /* pixel opcional */ }
      const { data } = await api.post("/payments/asaas/checkout", {
        back_origin: window.location.origin,
        cpf_cnpj: digits,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(typeof msg === "string" ? msg : "Erro ao iniciar checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="checkout-modal">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="checkout-close"><X size={22}/></button>
        <h2 className="font-heading text-2xl font-extrabold text-slate-900">Finalizar assinatura</h2>
        <p className="text-slate-600 text-sm mt-1">Precisamos do seu CPF ou CNPJ para emitir a cobrança no Asaas. Nenhum dado de cartão é armazenado pelo AulaPronta.</p>

        <div className="mt-6">
          <Label className="text-sm font-semibold text-slate-700">CPF ou CNPJ</Label>
          <Input
            value={cpf}
            onChange={(e) => setCpf(formatCpfCnpj(e.target.value))}
            placeholder="000.000.000-00"
            inputMode="numeric"
            autoFocus
            className="mt-1 rounded-xl h-12"
            data-testid="checkout-cpf-input"
          />
          <p className="mt-2 text-xs text-slate-500">Utilizado apenas para emissão da cobrança oficial pelo Asaas.</p>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-2 p-2 rounded-xl bg-slate-50"><QrCode size={14} className="text-[#1F3A8A]"/> PIX instantâneo</div>
          <div className="flex items-center gap-2 p-2 rounded-xl bg-slate-50"><CreditCard size={14} className="text-[#8B5CF6]"/> Cartão de crédito</div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="checkout-error">
            {error}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={!valid || loading}
          className="mt-6 w-full rounded-full bg-[#1F3A8A] hover:bg-[#15265b] py-6 text-base disabled:opacity-50"
          data-testid="checkout-confirm">
          <Sparkles className="mr-2" size={16}/>
          {loading ? "Abrindo Asaas..." : "Ir para o Checkout seguro"}
        </Button>

        <p className="mt-3 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1">
          <ShieldCheck size={12} className="text-emerald-600"/> Checkout hospedado pelo Asaas · HTTPS · PCI-DSS
        </p>
      </div>
    </div>
  );
}

export function AssinaturaPage() {
  const { user, usage, refreshUsage } = useAuth();
  const premium = user?.plan === "premium";
  const source = user?.subscription_source;
  const [modal, setModal] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");
  const charge = useCurrentCharge();

  const label = source === "manual" ? "Premium — Manual"
              : source === "asaas" ? "Premium — Asaas"
              : source === "payment" ? "Premium — Pagamento"
              : "Premium";

  const doResync = async () => {
    setResyncing(true); setResyncMsg("");
    try {
      const { data } = await api.post("/payments/asaas/resync");
      setResyncMsg(data.message);
      if (data.activated) {
        await refreshUsage?.();
        setTimeout(() => window.location.reload(), 1800);
      }
    } catch (e) {
      setResyncMsg(e?.response?.data?.detail || "Erro ao sincronizar com o Asaas.");
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Assinatura</h1>
      <p className="mt-2 text-slate-600">Tenha acesso completo às ferramentas do AulaPronta.</p>

      {premium && charge?.has_charge && (
        <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-white p-5 shadow-sm" data-testid="next-charge-card">
          <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row">
            <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
              charge.is_overdue ? "bg-red-100 text-red-700"
              : (charge.days_until_due !== null && charge.days_until_due <= 3) ? "bg-amber-100 text-amber-700"
              : "bg-emerald-100 text-emerald-700"
            }`}>
              <Calendar size={20}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                {charge.is_overdue ? "Pagamento vencido" : "Próxima cobrança"}
              </div>
              <div className="font-heading text-lg font-bold text-slate-900">
                {charge.due_date ? new Date(charge.due_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"}
                {" · "}R$ {Number(charge.value || 0).toFixed(2).replace(".", ",")}
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                {charge.is_overdue
                  ? "Após 3 dias úteis sem pagamento, a conta volta ao plano gratuito automaticamente."
                  : "Pague por PIX ou Cartão. O Premium continua ativo enquanto os pagamentos estiverem em dia."}
              </div>
            </div>
            {charge.invoice_url && (
              <a href={charge.invoice_url} target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 text-white font-bold px-4 py-2 rounded-full whitespace-nowrap text-sm ${
                  charge.is_overdue ? "bg-red-600 hover:bg-red-700"
                  : "bg-[#1F3A8A] hover:bg-[#15265b]"
                }`}
                data-testid="next-charge-pay">
                Pagar agora <ExternalLink size={14}/>
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 relative bg-white rounded-3xl p-8 border-2 border-[#8B5CF6] shadow-[0_20px_60px_rgba(139,92,246,0.15)]">
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FBBF24] text-[#0F172A] text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1">
          <Star size={12} fill="currentColor"/> POPULAR
        </div>
        <h3 className="font-heading text-2xl font-extrabold text-slate-900">AulaPronta Premium</h3>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-5xl font-heading font-extrabold text-[#1F3A8A]">R$ {(usage?.premium_price ?? 19.90).toFixed(2).replace(".", ",")}</span>
          <span className="text-slate-500">/mês</span>
        </div>
        <ul className="mt-6 space-y-2.5 text-sm">
          {["Plano de aula (limite mensal alto)","Todas as atividades e avaliações","Adaptação de conteúdo","Todas as disciplinas","Histórico completo","Exportação em PDF"].map(x => (
            <li key={x} className="flex items-center gap-2 text-slate-700"><Check className="text-[#8B5CF6]" size={16}/> {x}</li>
          ))}
        </ul>

        <div className="mt-5 flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><QrCode size={14} className="text-[#1F3A8A]"/> PIX</span>
          <span className="flex items-center gap-1"><CreditCard size={14} className="text-[#8B5CF6]"/> Cartão de crédito</span>
          <span className="ml-auto flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-600"/> Checkout Asaas</span>
        </div>

        {premium ? (
          <div className="mt-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 py-3 px-4 font-semibold text-center" data-testid="already-premium">
            ✓ Você já é {label}
          </div>
        ) : (
          <>
            <div
              className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-4"
              data-testid="payment-notice"
            >
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-800">Pagamento seguro</div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600">
                    O pagamento é processado pelo Asaas, em ambiente seguro.
                    O nome do responsável pelo AulaPronta poderá aparecer nos dados da cobrança,
                    conforme as exigências legais do processamento financeiro.
                  </p>
                  <p className="mt-2 text-[11px] text-slate-500">
                    PIX e cartão de crédito • Ambiente seguro • Checkout Asaas
                  </p>
                </div>
              </div>
            </div>
            <Button className="w-full mt-4 rounded-full bg-[#1F3A8A] hover:bg-[#15265b] py-6 text-base"
              onClick={() => setModal(true)}
              data-testid="btn-subscribe">
              <Sparkles className="mr-2" size={16}/> Assinar Premium
            </Button>
            <button
              onClick={doResync}
              disabled={resyncing}
              className="w-full mt-3 text-sm text-slate-600 hover:text-[#1F3A8A] underline-offset-2 hover:underline disabled:opacity-50"
              data-testid="btn-resync">
              {resyncing ? "Verificando no Asaas..." : "Já paguei? Clique aqui para sincronizar"}
            </button>
            {resyncMsg && (
              <div className={`mt-3 text-sm text-center ${resyncMsg.includes("reativado") ? "text-emerald-700" : "text-slate-600"}`} data-testid="resync-msg">
                {resyncMsg}
              </div>
            )}
          </>
        )}
        <p className="text-center text-xs text-slate-500 mt-3">Cobrança mensal recorrente processada pelo Asaas. Cancele quando quiser.</p>
      </div>

      <CheckoutModal open={modal} onClose={() => setModal(false)} user={user}/>
    </div>
  );
}
